"use client"

import { actionValidateSignupEmail, actionVerifySignup } from "@/app/api/signUp/verify/actions"
import type { TEnrichedPersonData } from "@/app/api/enrich/email/schema"
import { PhoneInput, validatePhoneNumber } from "@/components/generic/PhoneInput"
import { useEmailEnrichment } from "@/hooks/useEmailEnrichment"
import { showNotification } from "@/lib/notifications"
import { identifySignupPostHogUser } from "@/lib/posthog/identity"
import { SIGNUP_EXPERIMENT_FLAG_KEY, SIGNUP_EXPERIMENT_VARIANT } from "@/lib/posthog/signupExperiment.constants"
import { Anchor, Box, Button, PasswordInput, Popover, Stack, Text, TextInput, Title, UnstyledButton } from "@mantine/core"
import { useForm, zodResolver } from "@mantine/form"
import { useDisclosure } from "@mantine/hooks"
import { Check, Mail, Building2, UserRound, ArrowRight, Loader2 } from "lucide-react"
import { useTranslations } from "next-intl"
import Image from "next/image"
import { usePostHog } from "posthog-js/react"
import { useState, useTransition } from "react"
import { z } from "zod"
import { PasswordStrength } from "./PasswordStrength"
import { SignupSuccessOverlay } from "./SignupSuccessOverlay"
import { buildEmailValidationFailedEventProperties, isConsumerEmailDomain, splitFullName } from "./EmailFirstSignUp.helpers"
import { mapApiErrorsToFormErrors } from "./helpers"
import type { TApiSignupCreateParams, TSignupVerifyErrorField } from "@/api/launcherApp/signup/schema"
import classes from "./EmailFirstSignUp.module.css"

type EmailStep = "email" | "details"

const SHOW_SSO_BUTTONS = false

type EmailFirstSignUpValues = {
  company: string
  email: string
  fullName: string
  password: string
  phone: string
  promotionalEmails: boolean
  termsAccepted: boolean
  username: string
}

const detailsSchema = z.object({
  company: z.string().min(2, "Company name must be at least 2 characters"),
  email: z
    .string()
    .email("Invalid email address")
    .refine((value) => !isConsumerEmailDomain(value), "Please use your work email"),
  fullName: z
    .string()
    .min(2, "Full name must be at least 2 characters")
    .refine((value) => Boolean(splitFullName(value).firstName && splitFullName(value).lastName), "Enter your first and last name"),
  password: z
    .string()
    .min(8, "Password must be at least 8 characters")
    .regex(/[A-Z]/, "Password must contain at least one uppercase letter")
    .regex(/[a-z]/, "Password must contain at least one lowercase letter")
    .regex(/[0-9]/, "Password must contain at least one number")
    .regex(/[^A-Za-z0-9]/, "Password must contain at least one special character"),
  phone: z.string().refine((value) => validatePhoneNumber(value) !== "", "Invalid phone number"),
  promotionalEmails: z.boolean(),
  termsAccepted: z.boolean().refine((value) => value, "You must accept the terms and conditions"),
  username: z.string(),
})

function getDomain(email: string) {
  return email.trim().toLowerCase().split("@")[1] ?? ""
}

function mapSignupErrorToField(field: TSignupVerifyErrorField) {
  if (field === "firstName" || field === "lastName") {
    return "fullName"
  }

  if (field === "companyName") {
    return "company"
  }

  if (field === "phoneNumber") {
    return "phone"
  }

  if (field === "passwd") {
    return "password"
  }

  if (field === "emailMarketingOptIn") {
    return "promotionalEmails"
  }

  return field
}

export function EmailFirstSignUp() {
  const posthog = usePostHog()
  const t = useTranslations("auth.signUp")
  const tErrors = useTranslations("auth.errors")
  const [step, setStep] = useState<EmailStep>("email")
  const [emailError, setEmailError] = useState<string | null>(null)
  const [isEmailValidationPending, setIsEmailValidationPending] = useState(false)
  const [isPending, startTransition] = useTransition()
  const [isPasswordFocused, setIsPasswordFocused] = useState(false)
  const [showSuccessOverlay, { open: openSuccessOverlay, close: closeSuccessOverlay }] = useDisclosure(false)
  const [createParams, setCreateParams] = useState<TApiSignupCreateParams | undefined>(undefined)
  const [enrichmentData, setEnrichmentData] = useState<TEnrichedPersonData | null>(null)

  const form = useForm<EmailFirstSignUpValues>({
    initialValues: {
      company: "",
      email: "",
      fullName: "",
      password: "",
      phone: "",
      promotionalEmails: true,
      termsAccepted: true,
      username: "",
    },
    validate: zodResolver(detailsSchema),
  })

  const { enrich: enrichEmail, isLoading: isEnrichmentLoading } = useEmailEnrichment({
    onSuccess: (data) => {
      setEnrichmentData(data)
      const fullName = [data.firstName, data.lastName].filter(Boolean).join(" ")
      if (fullName) {
        form.setFieldValue("fullName", fullName)
      }
      if (data.company) {
        form.setFieldValue("company", data.company)
      }
    },
  })

  const validateEmailStep = () => {
    const email = form.values.email.trim()
    const parsedEmail = z.string().email().safeParse(email)

    if (!parsedEmail.success) {
      setEmailError("Email appears to be invalid")
      return false
    }

    if (isConsumerEmailDomain(email)) {
      setEmailError("consumer")
      return false
    }

    setEmailError(null)
    return true
  }

  const handleContinue = async () => {
    if (isEmailValidationPending) {
      return
    }

    if (!validateEmailStep()) {
      return
    }

    const normalizedEmail = form.values.email.trim().toLowerCase()
    form.setFieldValue("email", normalizedEmail)

    setIsEmailValidationPending(true)
    const validationResult = await actionValidateSignupEmail(normalizedEmail)
    setIsEmailValidationPending(false)

    if (!validationResult.success) {
      const translatedErrors = validationResult.error ? mapApiErrorsToFormErrors([validationResult.error], tErrors) : {}
      setEmailError(translatedErrors.email ?? validationResult.error?.message ?? "We could not validate this email. Please try again.")
      posthog.capture("signup_email_first_email_validation_failed", {
        signupExperimentKey: SIGNUP_EXPERIMENT_FLAG_KEY,
        signupExperimentVariant: SIGNUP_EXPERIMENT_VARIANT,
        ...buildEmailValidationFailedEventProperties(normalizedEmail),
        errorCode: validationResult.error?.code,
      })
      return
    }

    setStep("details")
    void enrichEmail(normalizedEmail)
    identifySignupPostHogUser({
      client: posthog,
      currentDistinctId: posthog.get_distinct_id(),
      storage: typeof window !== "undefined" ? window.localStorage : null,
      userData: {
        email: normalizedEmail,
        plan: "solo",
        company: "",
        signupFlow: "email_first",
        signupExperimentKey: SIGNUP_EXPERIMENT_FLAG_KEY,
        signupExperimentVariant: SIGNUP_EXPERIMENT_VARIANT,
      },
    })
    posthog.capture("signup_email_first_email_validated", {
      signupExperimentKey: SIGNUP_EXPERIMENT_FLAG_KEY,
      signupExperimentVariant: SIGNUP_EXPERIMENT_VARIANT,
      emailDomain: getDomain(normalizedEmail),
    })
  }

  const handleSubmit = (values: EmailFirstSignUpValues) => {
    if (values.username) {
      window.location.href = "https://www.google.com"
      return
    }

    const { firstName, lastName } = splitFullName(values.fullName)

    startTransition(async () => {
      const result = await actionVerifySignup({
        firstName,
        lastName,
        email: values.email,
        phoneNumber: values.phone,
        companyName: values.company,
        smsOptIn: true,
        emailMarketingOptIn: values.promotionalEmails,
        trialType: "solo",
        passwd: values.password,
      })

      if (result.success === false || !result.data?.success) {
        result.data?.errors?.forEach((error) => {
          form.setFieldError(mapSignupErrorToField(error.field), error.defaultMessage)
        })

        if (!result.data?.errors?.length) {
          showNotification({
            type: "error",
            title: t("genericError.title"),
            message: t("genericError.message"),
          })
        }
        return
      }

      setCreateParams(result.data.cookies ?? [])
      openSuccessOverlay()
    })
  }

  const handleSuccess = () => {
    closeSuccessOverlay()
    setCreateParams(undefined)
  }

  const handleError = () => {
    closeSuccessOverlay()
    setCreateParams(undefined)
    showNotification({
      type: "error",
      autoClose: 10000,
      title: t("signupError.title"),
      message: t("signupError.message"),
    })
  }

  const { firstName, lastName } = splitFullName(form.values.fullName)
  const isEmailStepBusy = isPending || isEmailValidationPending

  return (
    <main className={classes.page}>
      <Box component="header" className={classes.topbar}>
        <Image className={classes.logo} src="/assets/ringio.png" alt="Ring.io" width={110} height={40} priority unoptimized />
        <Box className={classes.topbarRight}>
          Already have an account?<Anchor href="/login">Log in</Anchor>
        </Box>
      </Box>

      <Box className={classes.body}>
        <section className={classes.formColumn}>
          <Box className={classes.formShell}>
            <Title order={1} className={classes.title}>
              {step === "details" ? "A few details" : "Get started"}
            </Title>
            <Text className={classes.subtitle}>{step === "details" ? "We pulled these from your company profile. Edit anything that's wrong." : "Enter your work email to continue. We'll help you create your Ring.io account."}</Text>

            {step === "email" ? (
              <Stack gap="md">
                <TextInput
                  autoFocus
                  disabled={isEmailStepBusy}
                  error={emailError && emailError !== "consumer" ? emailError : undefined}
                  id="signup-email-first-work-email"
                  label="Work email"
                  leftSection={<Mail size={16} />}
                  placeholder="jane@acme.com"
                  required
                  size="md"
                  value={form.values.email}
                  onChange={(event) => {
                    form.setFieldValue("email", event.currentTarget.value)
                    setEmailError(null)
                  }}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") {
                      event.preventDefault()
                      void handleContinue()
                    }
                  }}
                />

                {emailError === "consumer" ? (
                  <Box className={classes.blocked}>
                    <Text component="strong">Ring.io is for sales professionals.</Text>
                    <br />
                    {"Please use your work email. We don't accept gmail.com, yahoo.com, outlook.com or other personal email services."}
                  </Box>
                ) : null}

                <Button fullWidth loading={isEmailValidationPending} size="md" rightSection={<ArrowRight size={16} />} onClick={() => void handleContinue()}>
                  Continue
                </Button>

                {SHOW_SSO_BUTTONS ? (
                  <>
                    <Box className={classes.divider}>{emailError === "consumer" ? "Or use SSO" : "or"}</Box>
                    <DisabledSsoStack />
                  </>
                ) : null}

                <Text className={classes.finePrint}>
                  By continuing you agree to our <Anchor href="https://www.ring.io/terms-and-conditions/">Terms</Anchor> and <Anchor href="https://www.ring.io/privacy-policy/">Privacy Policy</Anchor>.
                </Text>
              </Stack>
            ) : (
              <form className={classes.form} onSubmit={form.onSubmit(handleSubmit)}>
                <TextInput
                  disabled={isPending || isEnrichmentLoading}
                  id="signup-email-first-full-name"
                  label="Full name"
                  leftSection={<UserRound size={16} />}
                  placeholder="Jane Doe"
                  required
                  rightSection={isEnrichmentLoading ? <Loader2 size={16} className={classes.spin} /> : undefined}
                  size="md"
                  {...form.getInputProps("fullName")}
                />
                <TextInput disabled={isPending || isEnrichmentLoading} id="signup-email-first-company" label="Company" leftSection={<Building2 size={16} />} placeholder="Acme Corp" required size="md" {...form.getInputProps("company")} />
                <PhoneInput
                  allowedCountries={false}
                  disabled={isPending}
                  error={form.errors.phone}
                  id="signup-email-first-phone"
                  label="Phone"
                  onChangePhone={(value) => form.setFieldValue("phone", value)}
                  placeholder="+1 415 555 0117"
                  required
                  size="md"
                  value={form.values.phone}
                />

                <Popover opened={isPasswordFocused} position="right-end" width={340} shadow="md" withArrow withinPortal={false}>
                  <Popover.Target>
                    <PasswordInput
                      disabled={isPending}
                      id="signup-email-first-password"
                      label="Password"
                      placeholder={t("passwordPlaceholder")}
                      required
                      size="md"
                      {...form.getInputProps("password")}
                      onBlur={(event) => {
                        form.getInputProps("password").onBlur?.(event)
                        setIsPasswordFocused(false)
                      }}
                      onFocus={(event) => {
                        form.getInputProps("password").onFocus?.(event)
                        setIsPasswordFocused(true)
                      }}
                    />
                  </Popover.Target>
                  <Popover.Dropdown className={classes.passwordPopover}>
                    <PasswordStrength inPopover password={form.values.password} />
                  </Popover.Dropdown>
                </Popover>

                <TextInput disabled={isPending} id="signup-email-first-username" style={{ display: "none" }} aria-hidden="true" tabIndex={-1} {...form.getInputProps("username")} />

                <Button className={classes.createAccountButton} fullWidth loading={isPending} rightSection={<ArrowRight size={16} />} size="md" type="submit">
                  Create my account
                </Button>
              </form>
            )}
          </Box>
        </section>

        <MarketingColumn step={step} />
      </Box>

      <SignupSuccessOverlay
        onSuccess={handleSuccess}
        onError={handleError}
        userData={{
          email: form.values.email,
          password: form.values.password,
          plan: "solo",
          company: form.values.company,
          companyCountry: enrichmentData?.companyCountry ?? undefined,
          companySize: enrichmentData?.companySize ?? undefined,
          emailMarketingOptIn: form.values.promotionalEmails,
          firstName,
          lastName,
          phone: form.values.phone,
          signupFlow: "email_first",
          smsOptIn: true,
          signupExperimentKey: SIGNUP_EXPERIMENT_FLAG_KEY,
          signupExperimentVariant: SIGNUP_EXPERIMENT_VARIANT,
        }}
        enrichmentData={enrichmentData}
        createParams={createParams}
        opened={showSuccessOverlay}
        onClose={closeSuccessOverlay}
      />
    </main>
  )
}

function DisabledSsoStack() {
  return (
    <Box className={classes.ssoStack}>
      <UnstyledButton className={classes.ssoButton} disabled title="SSO signup is not available yet" type="button">
        <MicrosoftMark />
        Continue with Microsoft
      </UnstyledButton>
      <UnstyledButton className={classes.ssoButton} disabled title="SSO signup is not available yet" type="button">
        <GoogleMark />
        Continue with Google Workspace
      </UnstyledButton>
      <Text className={classes.ssoNote}>{"Google Workspace only. We don't accept personal gmail.com addresses."}</Text>
    </Box>
  )
}

function MarketingColumn({ step }: { step: EmailStep }) {
  const details = step === "details"

  return (
    <Box component="aside" className={classes.marketing} data-step={step}>
      <Text className={classes.eyebrow}>{details ? "Workspace setup" : "Built for sales teams"}</Text>
      <Title order={2} className={classes.marketingTitle}>
        {details ? "Your calling workspace is almost ready" : "Turn more calls into pipeline"}
      </Title>
      <Box component="ul" className={classes.bullets}>
        {(details
          ? ["Confirm your team profile", "Start from a ready-to-call workspace", "Keep call notes, recordings, and CRM activity together"]
          : ["Power dial through prioritized lead lists", "AI summaries, recordings, and CRM updates after every call", "Call, text, and follow up from one Ring.io workspace"]
        ).map((item) => (
          <Box component="li" key={item}>
            <Check className={classes.bulletIcon} size={14} strokeWidth={3} />
            <Text component="span">{item}</Text>
          </Box>
        ))}
      </Box>

      <AppPreview step={step} />
    </Box>
  )
}

function MicrosoftMark() {
  return (
    <svg width="18" height="18" viewBox="0 0 23 23" aria-hidden="true">
      <rect x="0" y="0" width="10" height="10" fill="#f25022" />
      <rect x="12" y="0" width="10" height="10" fill="#7fba00" />
      <rect x="0" y="12" width="10" height="10" fill="#00a4ef" />
      <rect x="12" y="12" width="10" height="10" fill="#ffb900" />
    </svg>
  )
}

function GoogleMark() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
      <path fill="#4285f4" d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.49h4.84c-.21 1.12-.84 2.07-1.79 2.71v2.26h2.9c1.7-1.56 2.69-3.87 2.69-6.62z" />
      <path fill="#34a853" d="M9 18c2.43 0 4.47-.81 5.96-2.18l-2.9-2.26c-.81.54-1.83.86-3.06.86-2.36 0-4.36-1.59-5.07-3.74H.96v2.34A9 9 0 0 0 9 18z" />
      <path fill="#fbbc05" d="M3.93 10.68A5.4 5.4 0 0 1 3.64 9c0-.58.1-1.15.29-1.68V4.98H.96A9 9 0 0 0 0 9c0 1.45.35 2.82.96 4.02l2.97-2.34z" />
      <path fill="#ea4335" d="M9 3.58c1.32 0 2.51.46 3.45 1.35l2.58-2.58A8.96 8.96 0 0 0 9 0 9 9 0 0 0 .96 4.98l2.97 2.34C4.64 5.17 6.64 3.58 9 3.58z" />
    </svg>
  )
}

function AppPreview({ step }: { step: EmailStep }) {
  const previewSrc = step === "details" ? "/assets/signup-power-dialer-preview.webp" : "/assets/signup-messenger-preview-v2.webp"

  return (
    <Box className={classes.previewStage} aria-hidden="true">
      <Box className={classes.appPreview}>
        <Image className={classes.previewImage} src={previewSrc} alt="" width={1440} height={900} sizes="(min-width: 981px) 58vw, 0vw" priority unoptimized />
      </Box>
    </Box>
  )
}
